import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { toast } from 'react-hot-toast';
import { Notification, NotificationType, Permission } from '@hr-outsourcing/shared';
import { API_URL } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { translateNotificationContent } from '@/lib/notificationContent';
import { queryKeys } from '@/lib/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { getToken } from '@/state/authState';
import { normalizeNotification } from '@/lib/notificationNormalizer';
import { useHasActiveSeat } from '@/hooks/useHasActiveSeat';

const REFRESH_THRESHOLD = 5000;
const DASHBOARD_TRIGGER_TYPES = new Set([
  NotificationType.EXPENSE_REQUEST_CREATED,
  NotificationType.EXPENSE_REQUEST_PROCESSED,
  NotificationType.EXPENSE_CREATED,
  NotificationType.EXPENSE_APPROVED,
  NotificationType.EXPENSE_REJECTED,
  NotificationType.LEAVE_REQUEST_CREATED,
  NotificationType.LEAVE_REQUEST_PROCESSED,
  NotificationType.LEAVE_APPROVED,
  NotificationType.LEAVE_REJECTED,
  NotificationType.OVERTIME_REQUEST_CREATED,
  NotificationType.OVERTIME_REQUEST_PROCESSED,
  NotificationType.OVERTIME_CREATED,
  NotificationType.OVERTIME_APPROVED,
  NotificationType.OVERTIME_REJECTED,
]);
export const useNotificationInitializer = () => {
  const { user, hasAnyPermission } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const isConnectedRef = useRef<boolean>(false);
  const lastLeaveTimeRef = useRef<number>(Date.now());
  const hasActiveSeat = useHasActiveSeat();
  const isHR = hasAnyPermission([
    Permission.LEAVES_READ_ALL,
    Permission.EXPENSE_READ_ALL,
    Permission.OVERTIME_READ_ALL,
  ]);
  useEffect(() => {
    if (!user || !hasActiveSeat) return;
    const token = getToken();
    if (!token) return;
    const ctrl = new AbortController();
    if (isHR && !user.isSuperAdmin) {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.pendingApprovals() });
    }
    fetchEventSource(`${API_URL}/events`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl.signal,
      openWhenHidden: true,
      onopen: async (response) => {
        if (response.ok) {
          isConnectedRef.current = true;
          queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
          if (isHR && !user.isSuperAdmin) {
            queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.pendingApprovals() });
          }
          return;
        }
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          ctrl.abort();
        }
      },
      onmessage: (event) => {
        isConnectedRef.current = true;
        if (event.event && event.event !== 'message') {
          return;
        }
        try {
          if (!event.data) return;
          const newNotification = normalizeNotification(JSON.parse(event.data) as Notification);
          const notificationKeys = queryKeys.notifications.scoped(user.id);
          if (
            newNotification.type === NotificationType.NEW_ANNOUNCEMENT ||
            newNotification.type === NotificationType.UPDATED_ANNOUNCEMENT
          ) {
            queryClient.invalidateQueries({ queryKey: queryKeys.announcements.all });
          }
          const isDashboardTrigger = DASHBOARD_TRIGGER_TYPES.has(newNotification.type) && isHR && !user.isSuperAdmin;
          if (isDashboardTrigger) {
            queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.pendingApprovals() });
          }
          queryClient.setQueryData<Notification[]>(notificationKeys.listKey, (old) => [
            newNotification,
            ...(old ?? []),
          ]);
          queryClient.setQueryData<number>(notificationKeys.unreadCountKey, (old) =>
            newNotification.isRead ? (old ?? 0) : (old ?? 0) + 1
          );
          const toastTitle = translateNotificationContent(newNotification.content, tRef.current);
          if (toastTitle && !isDashboardTrigger) {
            toast.success(toastTitle, { icon: '🔔' });
          }
          if (document.hidden) {
            document.title = tRef.current('notifications.newNotificationTitle');
          }
        } catch (error) {
          console.error(tRef.current('notifications.sseMessageError'), error);
        }
      },
      onclose: () => {
        isConnectedRef.current = false;
      },
      onerror: (err) => {
        console.error(tRef.current('notifications.sseError'), err);
        isConnectedRef.current = false;
      },
    });
    return () => {
      ctrl.abort();
      isConnectedRef.current = false;
    };
  }, [user?.id, isHR, queryClient, hasActiveSeat]);
  useEffect(() => {
    if (!user) return;
    const handleVisibilityChange = () => {
      const now = Date.now();
      if (document.visibilityState === 'hidden') {
        lastLeaveTimeRef.current = now;
      } else if (document.visibilityState === 'visible') {
        const timeAway = now - lastLeaveTimeRef.current;
        const shouldFetch = !isConnectedRef.current || timeAway > REFRESH_THRESHOLD;
        if (shouldFetch && !user.isSuperAdmin) {
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
            if (isHR && !user.isSuperAdmin) {
              queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.pendingApprovals() });
            }
          }, 500);
        }
        document.title = 'Peoply';
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [user, isHR, queryClient]);
};
